document.addEventListener("alpine:init", () => {
  Alpine.data("colorSelector", () => ({
    loading: true,
    colors: [],
    currentColor: "",
    debug: false, // Set to true to show debug info
    observer: null,

    // Preload critical images
    preloadImage(url) {
      if (!url) return;
      const img = new Image();
      img.src = url;
    },

    // Optimize image URL for better performance
    optimizeImageUrl(originalUrl, width = 100, height = 100) {
      if (!originalUrl) return null;

      // If the URL already has parameters, append to them
      const separator = originalUrl.includes("?") ? "&" : "?";
      return `${originalUrl}${separator}w=${width}&h=${height}&fit=crop&auto=format,compress&q=80`;
    },

    // Initialize intersection observer for lazy loading
    initIntersectionObserver() {
      if ("IntersectionObserver" in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                const img = entry.target;
                if (img.dataset.src) {
                  img.src = img.dataset.src;
                  img.removeAttribute("data-src");
                  this.observer.unobserve(img);
                }
              }
            });
          },
          {
            rootMargin: "50px 0px",
            threshold: 0.1,
          },
        );
      }
    },

    // Get current page's selected variant
    getCurrentVariant() {
      // First try to get from Liquid data
      const currentVariantId = window.colorSelectorData?.currentVariantId;
      if (currentVariantId) {
        return currentVariantId;
      }

      // Fallback to URL parameter
      const urlParams = new URLSearchParams(window.location.search);
      const variantIdFromUrl = urlParams.get("id");

      // If no URL param, try to get from Alpine data if available
      if (!variantIdFromUrl && window.Alpine && window.Alpine.store) {
        const productData = document.querySelector('[x-data*="product("]');
        if (productData && productData._x_dataStack) {
          const productComponent = productData._x_dataStack[0];
          if (productComponent && productComponent.selectedVariantID) {
            return productComponent.selectedVariantID;
          }
        }
      }

      return variantIdFromUrl;
    },

    // Find matching variant in target product by title
    findMatchingVariant(targetProduct, currentVariantTitle) {
      if (!targetProduct.variants || !currentVariantTitle) return null;

      return targetProduct.variants.find(
        (variant) => variant.title === currentVariantTitle,
      );
    },

    // Get current variant title from current product
    getCurrentVariantTitle() {
      const currentVariantId = this.getCurrentVariant();
      if (!currentVariantId) return null;

      const collectionProducts = window.colorSelectorData?.collectionProducts;
      if (!collectionProducts) return null;

      // Use current product URL from Liquid data if available
      const currentProductUrl =
        window.colorSelectorData?.currentProductUrl ||
        window.location.pathname.replace(".html", "").replace("/", "");

      const currentProduct = collectionProducts.find(
        (p) => p.url === currentProductUrl,
      );

      if (!currentProduct || !currentProduct.variants) return null;

      const currentVariant = currentProduct.variants.find(
        (v) => String(v.externalId) === String(currentVariantId),
      );

      return currentVariant ? currentVariant.title : null;
    },

    // Handle click event to add scroll position to URL
    handleColorClick(event, originalUrl) {
      event.preventDefault();

      const scrollTop = window.pageYOffset;
      let finalUrl = originalUrl;

      if (scrollTop > 0) {
        const url = new URL(originalUrl, window.location.origin);
        url.searchParams.set("scrollPos", String(scrollTop));
        finalUrl = url.toString();
      }

      window.location.href = finalUrl;
    },

    async initColorSelector() {
      try {
        // Initialize intersection observer for lazy loading
        this.initIntersectionObserver();

        // Get current product color from the global data passed from Liquid
        const currentProductColors =
          window.colorSelectorData?.currentProductColors;
        if (currentProductColors && currentProductColors.length > 0) {
          this.currentColor = currentProductColors[0].value;
        }

        // Get current product shape from the global data passed from Liquid
        const currentProductShape =
          window.colorSelectorData?.currentProductShape;
        const currentShapeValue = currentProductShape?.value;

        // Collection products to fetch from global data
        const collectionProducts = window.colorSelectorData?.collectionProducts;
        if (!collectionProducts || collectionProducts.length === 0) {
          this.loading = false;
          return;
        }

        // Create comma-separated list of product URLs
        const productUrls = collectionProducts.map((p) => p.url).join(",");

        // Call our server-side API endpoint with timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

        const response = await fetch(
          `/api/product/collection-colors?products=${encodeURIComponent(productUrls)}`,
          { signal: controller.signal },
        );

        clearTimeout(timeoutId);

        if (!response.ok) {
          console.error("Failed to fetch collection colors:", response.status);
          this.loading = false;
          return;
        }

        const apiData = await response.json();

        if (!apiData.success) {
          console.error("API returned error:", apiData.error);
          this.loading = false;
          return;
        }

        // Filter products by shape and process the returned product details
        const productDetails = apiData.products
          .filter((product) => {
            // Only include products that have the same shape as the current product
            if (!currentShapeValue) {
              return true; // If no current shape, include all products
            }
            return product.specifications.shape?.value === currentShapeValue;
          })
          .map((product) => {
            return {
              url: product.url,
              title: product.title,
              colors: product.specifications.colors || [],
            };
          });

        // Process products - show each product with its image
        const currentVariantTitle = this.getCurrentVariantTitle();
        const currentProductUrl =
          window.colorSelectorData?.currentProductUrl ||
          window.location.pathname.replace(".html", "").replace("/", "");

        const productOptions = productDetails.map((detail) => {
          // Build URL with variant preservation (without scroll position - that's added on click)
          let finalUrl = detail.url + ".html";
          // If there's a current variant, try to find matching variant in target product
          if (currentVariantTitle) {
            const targetProduct = apiData.products.find(
              (p) => p.url === detail.url,
            );
            if (targetProduct) {
              const matchingVariant = this.findMatchingVariant(
                targetProduct,
                currentVariantTitle,
              );
              if (matchingVariant) {
                finalUrl = `${detail.url}.html?id=${matchingVariant.externalId}`;
              }
            }
          }

          // Find the 2nd image from the target product
          const targetProduct = apiData.products.find(
            (p) => p.url === detail.url,
          );
          let imageUrl = null;
          if (targetProduct && targetProduct.media) {
            // Filter to only get images
            const images = targetProduct.media.filter(
              (media) => media.media_type === "image",
            );
            // Get the 2nd image (index 1)
            if (images.length > 1) {
              imageUrl = this.optimizeImageUrl(images[1].image_url, 100, 100);
            }
            // Fallback to first image if there's no 2nd image
            if (!imageUrl && images.length > 0) {
              imageUrl = this.optimizeImageUrl(images[0].image_url, 100, 100);
            }
          }

          // Check if this is the current product
          const isCurrent = detail.url === currentProductUrl;

          return {
            url: finalUrl,
            isCurrent: isCurrent,
            displayValue: detail.title, // Use product title for accessibility
            imageUrl: imageUrl,
            imageLoaded: false, // Track image loading state
            colors: detail.colors,
          };
        });

        this.colors = productOptions.sort((a, b) =>
          a.displayValue.localeCompare(b.displayValue),
        );

        // Preload the current product's image for better UX
        const currentProduct = this.colors.find((color) => color.isCurrent);
        if (currentProduct && currentProduct.imageUrl) {
          this.preloadImage(currentProduct.imageUrl);
        }

        this.loading = false;
      } catch (error) {
        if (error.name === "AbortError") {
          console.error(
            "Request timeout - color selector data fetch took too long",
          );
        } else {
          console.error("Error loading color selector:", error);
        }
        this.loading = false;
      }
    },

    // Cleanup observer when component is destroyed
    destroy() {
      if (this.observer) {
        this.observer.disconnect();
      }
    },
  }));
});
